import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, CheckCircle2 } from 'lucide-react';

const suburbs = [
  { name: 'Katoomba', highlight: true },
  { name: 'Leura', highlight: true },
  { name: 'Springwood', highlight: false },
  { name: 'Blackheath', highlight: true },
  { name: 'Wentworth Falls', highlight: true },
  { name: 'Hazelbrook', highlight: false },
  { name: 'Blaxland', highlight: false },
  { name: 'Glenbrook', highlight: false },
  { name: 'Lawson', highlight: false },
  { name: 'Faulconbridge', highlight: false },
  { name: 'Winmalee', highlight: false },
  { name: 'Valley Heights', highlight: false },
];

export default function SuburbsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      id="areas"
      className="py-20 md:py-32 bg-[#111A2E] relative overflow-hidden"
    >
      {/* Background Map Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M50 0L100 50L50 100L0 50Z' fill='none' stroke='%2338BDF8' stroke-width='0.5'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-[#38BDF8] text-sm font-medium mb-6">
              <MapPin className="w-4 h-4" />
              Service Areas
            </span>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Proudly Serving the{' '}
              <span className="text-[#38BDF8]">Blue Mountains</span>
            </h2>
            
            <p className="text-lg text-[#94A3B8] mb-8">
              From the upper mountains of Katoomba to the lower mountains of Glenbrook, 
              we provide professional roof painting services across the entire Blue Mountains region.
            </p>

            <div className="space-y-4 mb-8">
              {[
                'Free on-site inspections throughout the Blue Mountains',
                'Local team with 25+ years of experience',
                'Understanding of local climate and conditions',
                'Fast response times for all suburbs'
              ].map((item, index) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#22C55E] flex-shrink-0 mt-0.5" />
                  <span className="text-[#94A3B8]">{item}</span>
                </motion.div>
              ))}
            </div>

            <motion.a
              href="#quote"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 cta-button px-8 py-4 rounded-full text-white font-semibold"
            >
              Get a Quote for Your Area
            </motion.a>
          </motion.div>

          {/* Right - Suburbs Grid */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {suburbs.map((suburb, index) => (
                <motion.div
                  key={suburb.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.4, delay: 0.3 + index * 0.05 }}
                  whileHover={{ scale: 1.05, y: -4 }}
                  className={`glass rounded-xl p-4 text-center cursor-pointer transition-all ${
                    suburb.highlight 
                      ? 'border border-[#38BDF8]/30' 
                      : 'border border-transparent hover:border-white/10'
                  }`}
                >
                  <MapPin className={`w-5 h-5 mx-auto mb-2 ${
                    suburb.highlight ? 'text-[#38BDF8]' : 'text-[#94A3B8]'
                  }`} />
                  <span className={`text-sm font-medium ${
                    suburb.highlight ? 'text-white' : 'text-[#94A3B8]'
                  }`}>
                    {suburb.name}
                  </span>
                </motion.div>
              ))}
            </div>

            <p className="text-center text-sm text-[#94A3B8] mt-6">
              Don't see your suburb? We likely cover it too!{' '}
              <a href="#quote" className="text-[#38BDF8] hover:underline">Contact us</a>
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
